import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import dateFormat from "dateformat";
import { useSelector } from "react-redux";
import useCreateQuoteMember from "../Hook/useCreateQuoteMember";
import { useDropdownValues } from "../Hook/useQuoteOptions";
import "../../Orders/Order.scss";

const genderOptions = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

const statusOptions = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

// Same picker as the Orders date range filter, in single-date mode
const dobPickerOptions = {
  maxDate: new Date(),
  dateFormat: "d M Y",
  minDate: new Date("01-01-1900"),
};

const initialMember = {
  name: "",
  email_id: "",
  contact: "",
  gender: "",
  birth_date: "",
  address: "",
  pincode: "",
  state: "",
  city: "",
  status: "ACTIVE",
  emp_code: "",
};

// Member form for the Convert to Order flow. Field keys match the AG member
// entity used by the AG Member screen.
const NewMemberModal = ({ show, onHide, onCreated }) => {
  const userInfo = useSelector((state) => state.common.userInfo);
  const [member, setMember] = useState(initialMember);
  const { mutate: createMember, isPending } = useCreateQuoteMember();
  const { data: stateOptions = [] } = useDropdownValues("AGM", "state", show);
  const { data: cityOptions = [] } = useDropdownValues("AGM", "city", show);

  useEffect(() => {
    if (show) {
      setMember(initialMember);
    }
  }, [show]);

  const setField = (key, value) => setMember((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    createMember(
      { ...member, name: member.name.trim(), rm_id: userInfo?.entityid },
      {
        onSuccess: (data) => {
          onCreated({
            value: data?.entityid,
            label: `${member.name.trim()}${
              member.email_id ? ` (${member.email_id})` : ""
            }`,
          });
        },
      }
    );
  };

  const textField = (label, key, type = "text", extra = {}) => (
    <Form.Group as={Col} md={6} className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control
        type={type}
        placeholder="Enter"
        value={member[key]}
        onChange={(e) => setField(key, e.target.value)}
        {...extra}
      />
    </Form.Group>
  );

  const selectField = (label, key, options) => (
    <Form.Group as={Col} md={6} className="mb-3">
      <Form.Label>{label}</Form.Label>
      <Select
        placeholder="Select"
        options={options}
        value={options.find((itm) => itm.value === member[key]) || null}
        onChange={(e) => setField(key, e ? e.value : "")}
        isClearable
      />
    </Form.Group>
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      keyboard={false}
      className="border-0"
      modalClassName="modal fade zoomIn"
      size="lg"
    >
      <Form className="attributes-container">
        <Modal.Header className="p-3 bg-info-subtle" closeButton>
          <strong>New Member</strong>
        </Modal.Header>
        <Modal.Body className="modal-body">
          <Row>
            {textField("Full Name", "name")}
            <Form.Group as={Col} md={6} className="mb-3">
              <Form.Label>Relationship Manager</Form.Label>
              <Form.Control type="text" value={userInfo?.name || ""} disabled />
            </Form.Group>
            {textField("Email id", "email_id", "email")}
            {textField("Mobile No.", "contact", "tel", {
              onChange: (e) =>
                setField("contact", e.target.value.replace(/\D/g, "")),
            })}
            {selectField("Gender", "gender", genderOptions)}
            <Form.Group as={Col} md={6} className="mb-3">
              <Form.Label>Date of Birth</Form.Label>
              <Flatpickr
                className="form-control border-lightgrey"
                placeholder="Select date"
                data-input
                options={dobPickerOptions}
                value={member.birth_date ? new Date(member.birth_date) : null}
                onChange={(dates) =>
                  setField(
                    "birth_date",
                    dates[0] ? dateFormat(dates[0], "yyyy-mm-dd") : ""
                  )
                }
              />
            </Form.Group>
            {textField("Address Line 1", "address")}
            {textField("Pincode", "pincode", "text", {
              onChange: (e) =>
                setField("pincode", e.target.value.replace(/\D/g, "")),
              maxLength: 6,
            })}
            {selectField("State", "state", stateOptions)}
            {selectField("City", "city", cityOptions)}
            {selectField("Status", "status", statusOptions)}
            {textField("Customer Code", "emp_code")}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" className="primary-outline" onClick={onHide}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={isPending}>
            {isPending ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default NewMemberModal;
